import React, { useContext, useEffect, useState } from "react";
import {
  ChildContainer,
  ParentContainer,
  GroupContainer,
  GroupText,
  DimensionText,
} from "./style";
import LoadingSpinner from "./LoadingSpinner";
import { CoreContainer, Notification } from "../common/style";
import { AppContext } from "../context";
import { getOrderCuttingPlan } from "./service";
import { useParams } from "react-router-dom";
import OptimizationToggleButton from "./OptimizationToggleButton";
import CuttingGroupCutlist from "./CuttingGroupCutlist";
import { useOutletContext } from "react-router-dom";
import CuttingPlanEditModal from "./CuttingPlanEditModal";

const CuttingPlan = () => {
  const { projectInfo: project, bomSummary } = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);
  const [currentGroup, setCurrentGroup] = useState(null);
  const { organizationData } = useContext(AppContext);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [optimization, setOptimization] = useState({});
  const [toggleState, setToggleState] = useState("info");
  const [isStale, setIsStale] = useState(false);
  const [isAdmin] = useOutletContext();

  const params = useParams();
  const projectId = params.projectId;

  const showCuttingPlan = (forceRefresh) => {
    setIsLoading(true);

    getOrderCuttingPlan({ projectId, forceRefresh, orderId: params.orderId })
      .then((response) => {
        const firstGroup = response.groups[0];
        setCurrentGroup(firstGroup);
        setOptimization(response);
        setIsStale(response.is_bom_stale);
      })
      .catch((error) => {
        setIsNotificationVisible(true);
        setNotification({ message: error.error, type: "error" });
      })
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    showCuttingPlan(false);
  }, []);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const toggleHandler = (newToggleState) => {
    setToggleState(newToggleState);
  };

  const renderPixels = (mmSize) => {
    return mmSize * 0.2;
  };

  const getBoardSizeText = (boardSize) => {
    switch (boardSize) {
      case "full_board":
        return "8x4";
      case "half_board_v":
        return "8x2";
      case "half_board_h":
        return "4x4";
    }
  };

  return (
    <>
      {isStale && (
        <div className="bg-dark-subtle text-danger p-2 text-center w-100 fw-bold fs-6 top-0 z-1 d-flex mt-3">
          <div className="flex-grow-1">Optimization is outdated</div>
        </div>
      )}
      {project.use_advanced_optimisation && (
        <div className="bg-dark-subtle text-danger p-2 text-center w-100 fw-bold fs-6 position-sticky top-0 z-1 mt-3">
          Using Advanced Optimization
        </div>
      )}
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      {isAdmin ? (
        isLoading ? (
          <CoreContainer>
            <LoadingSpinner />
          </CoreContainer>
        ) : (
          optimization.groups &&
          currentGroup && (
            <div className="row my-3">
              {project.use_advanced_optimisation && bomSummary.is_stale && (
                <div className="w-100 d-flex justify-content-start align-items-center pe-3 pb-3">
                  <div
                    className="border border-dark-subtle px-2 border rounded pointer"
                    onClick={() => {
                      showCuttingPlan(true);
                    }}
                  >
                    <span className="fa-solid fa-arrows-rotate fs-6"></span>
                    <span className="fs-5 ps-1">Refresh Optimisation</span>
                  </div>
                </div>
              )}

              <div className="w-100 d-flex justify-content-end align-items-center mt-3">
                <CuttingPlanEditModal
                  orderId={params.orderId}
                  groups={optimization.groups}
                />
              </div>

              <div
                className="col-5 p-0 overflow-scroll"
                style={{ border: "1px solid lightgray", height: "75vh" }}
              >
                {optimization.groups.map((group, index) => (
                  <GroupContainer
                    className={`d-flex justify-content-between p-2 pointer
                          ${
                            currentGroup.identifier === group.identifier &&
                            "active"
                          }`}
                    onClick={() => {
                      setToggleState("info");
                      setCurrentGroup(group);
                    }}
                    key={index}
                  >
                    <div>
                      <h6 className="fw-bold">{group.material_name}</h6>
                      <GroupText>{group.side_1_color}</GroupText>
                      <GroupText>{group.side_2_color}</GroupText>
                    </div>
                    <div className="text-end">
                      <GroupText className="fw-bold">
                        x {group.boards.length}
                      </GroupText>
                      <GroupText className="fw-bold">
                        {group.details.usage}%
                      </GroupText>
                    </div>
                  </GroupContainer>
                ))}
              </div>
              <div
                className="col-7 ps-5 pe-0 overflow-scroll"
                style={{ gap: "30px", height: "75vh" }}
              >
                <div>
                  <OptimizationToggleButton
                    onClick={(newToggleState) => toggleHandler(newToggleState)}
                    mode={toggleState}
                  />
                </div>
                {toggleState === "info" &&
                  optimization.groups.map(
                    (group, groupIndex) =>
                      currentGroup.identifier === group.identifier &&
                      group.boards.map((board, index) => (
                        <div key={index}>
                          <div
                            className="d-flex justify-content-between bg-secondary text-white p-2"
                            style={{
                              width: `${renderPixels(
                                board.details.length_2
                              )}px`,
                            }}
                          >
                            <h6 className="m-0">
                              Board {index + 1} -{" "}
                              {getBoardSizeText(board.details.size_label)}
                            </h6>
                            <h6 className="m-0">
                              {Math.round(board.details.usage)}%
                            </h6>
                          </div>
                          <ParentContainer
                            height={renderPixels(board.details.length_1)}
                            width={renderPixels(board.details.length_2)}
                            className="mb-3"
                          >
                            {board.parts.map((part, partIndex) => (
                              <ChildContainer
                                className={
                                  part.length_1 > part.length_2
                                    ? "flex-column"
                                    : "flex-row"
                                }
                                key={partIndex}
                                height={renderPixels(
                                  part.is_rotated &&
                                    project.use_advanced_optimisation
                                    ? part.length_2
                                    : part.length_1
                                )}
                                width={renderPixels(
                                  part.is_rotated &&
                                    project.use_advanced_optimisation
                                    ? part.length_1
                                    : part.length_2
                                )}
                                left={renderPixels(part.x)}
                                top={renderPixels(part.y)}
                                title={`${partIndex + 1} - ${part.length_1} x ${
                                  part.length_2
                                }`}
                              >
                                <p
                                  className="fw-bold bg-secondary text-white rounded-circle m-0 d-flex align-items-center justify-content-center"
                                  style={{
                                    padding: "1px 6px 1px 6px",
                                    width: "20px",
                                    height: "20px",
                                  }}
                                >
                                  {partIndex + 1}
                                </p>
                                <DimensionText
                                  isvertical={(part.length_2 < 330).toString()}
                                >
                                  {part.length_1} x {part.length_2}
                                </DimensionText>
                              </ChildContainer>
                            ))}
                          </ParentContainer>
                        </div>
                      ))
                  )}

                {toggleState === "cutlist" && (
                  <CuttingGroupCutlist
                    cuttingGroup={currentGroup}
                    projectId={project.id}
                    showcolor={true}
                  />
                )}
              </div>
            </div>
          )
        )
      ) : null}
    </>
  );
};

export default CuttingPlan;
